import { dev } from '$app/environment';
import { beforeNavigate, afterNavigate } from '$app/navigation';
import { PUBLIC_VOICE_ALIGN_WEBSITE } from '$env/static/public';
import { logger } from '@sentry/sveltekit';
import _ from 'lodash';
import { type PostHog } from 'posthog-js';

export type PosthogService = {
	init: () => Promise<void>;
	initNavigation: () => void;
	posthogReady: () => Promise<void>;
	updateConsent: (consent: boolean) => Promise<void>;
	getPosthog: () => Promise<PostHog | undefined>;
};

export function createPosthogService(): PosthogService {
	let resolvePosthogReadyPromise: () => void | undefined;
	let _posthogInitialized = false;
	let _posthog: PostHog | undefined = undefined;
	const posthogReadyPromise = new Promise<void>((resolve) => {
		resolvePosthogReadyPromise = resolve;
	});

	const apiHost = (() => {
		if (dev) {
			return 'https://eu.posthog.com';
		}
		return `${PUBLIC_VOICE_ALIGN_WEBSITE}/node-1050`;
	})();

	const initPosthog = _.once(async () => {
		const { posthog } = await import('posthog-js');
		posthog.init('phc_Mq73jMiRe3OV6bLKYBsxa8oRKmEOBDNQiFqZl3a3YFt', {
			api_host: apiHost,
			ui_host: 'https://eu.posthog.com',
			person_profiles: 'always', // or 'always' to create profiles for anonymous users as well
			capture_pageview: false,
			capture_pageleave: false,
			opt_out_capturing_by_default: true,
			opt_out_persistence_by_default: true
		});
		_posthogInitialized = true;
		_posthog = posthog;
		resolvePosthogReadyPromise();
	});

	return {
		async init() {
			await initPosthog();
		},
		initNavigation() {
			if (!_posthogInitialized) {
				logger.error('Posthog invalid init order');
				return;
			}
			beforeNavigate(() => _posthog?.capture('$pageleave'));
			afterNavigate(() => _posthog?.capture('$pageview'));
		},
		async posthogReady() {
			await posthogReadyPromise;
		},
		async updateConsent(consent: boolean) {
			await posthogReadyPromise;
			if (consent) {
				(await this.getPosthog())?.opt_in_capturing();
			} else {
				(await this.getPosthog())?.opt_out_capturing();
			}
		},
		async getPosthog() {
			await posthogReadyPromise;
			const { posthog } = await import('posthog-js');
			return posthog;
		}
	};
}
